import axios, { AxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { SESSION_ENDING_CODES, type ErrorCode, type ErrorParams } from '@immoaktas/shared';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | 'NETWORK',
    message: string,
    readonly field?: string,
    readonly details?: unknown,
    /** Specific translatable message (errors:server.<key>), see ServerMessage in @immoaktas/shared. */
    readonly key?: string,
    readonly params?: ErrorParams,
  ) {
    super(message);
  }
  /** Only auth codes end a session (B9). Network errors, 5xx, timeouts never do. */
  get endsSession() {
    return SESSION_ENDING_CODES.includes(this.code as ErrorCode);
  }
}

export type ClientOptions = {
  baseUrl: string;
  getAccessToken: () => string | null | Promise<string | null>;
  /** Returns a new access token or null if the session cannot be refreshed. */
  refresh: () => Promise<string | null>;
  onSessionEnd: () => void;
  getAccountId?: () => string | null;
  getImpersonate?: () => string | null;
  withCredentials?: boolean;
  timeoutMs?: number;
  /** Static headers on every request (e.g. X-Client for mobile version gates). */
  headers?: Record<string, string>;
  /** Current UI language, sent as Accept-Language so server mails match it. */
  getLanguage?: () => string | null | undefined;
};

export type Page<T> = { items: T[]; nextCursor: string | null };

export function createClient(o: ClientOptions) {
  const http: AxiosInstance = axios.create({ baseURL: `${o.baseUrl.replace(/\/$/, '')}/v1`, timeout: o.timeoutMs ?? 30_000, withCredentials: o.withCredentials ?? false });
  let refreshing: Promise<string | null> | null = null;

  async function send<T>(cfg: AxiosRequestConfig, retried = false): Promise<{ data: T; meta?: any; headers: Record<string, string> }> {
    const token = await o.getAccessToken();
    const headers: Record<string, string> = { ...o.headers, ...(cfg.headers as Record<string, string>) };
    if (token) headers.Authorization = `Bearer ${token}`;
    const lang = o.getLanguage?.();
    if (lang) headers['Accept-Language'] = lang;
    const acc = o.getAccountId?.();
    if (acc) headers['X-Account-Id'] = acc;
    const imp = o.getImpersonate?.();
    if (imp) headers['X-Impersonate-Account'] = imp;
    try {
      const r = await http.request({ ...cfg, headers });
      if (cfg.responseType === 'arraybuffer' || cfg.responseType === 'blob' || cfg.responseType === 'text') return { data: r.data as T, headers: r.headers as any };
      return { data: r.data.data as T, meta: r.data.meta, headers: r.headers as any };
    } catch (e) {
      const err = toApiError(e);
      if (err.code === 'TOKEN_EXPIRED' && !retried) {
        refreshing ??= o.refresh().finally(() => (refreshing = null));
        const t = await refreshing;
        if (t) return send<T>(cfg, true);
      }
      if (err.endsSession && err.code !== 'UNAUTHENTICATED') o.onSessionEnd();
      throw err;
    }
  }

  const call = async <T>(method: string, url: string, body?: unknown, extra: AxiosRequestConfig = {}) => (await send<T>({ method, url, data: body, ...extra })).data;
  return {
    http,
    get: <T>(url: string, params?: Record<string, unknown>) => call<T>('GET', url, undefined, { params: clean(params) }),
    post: <T>(url: string, body?: unknown, opts: { idempotencyKey?: string } = {}) => call<T>('POST', url, body ?? {}, opts.idempotencyKey ? { headers: { 'Idempotency-Key': opts.idempotencyKey } } : {}),
    patch: <T>(url: string, body: unknown, opts: { ifMatch?: number } = {}) => call<T>('PATCH', url, body, opts.ifMatch !== undefined ? { headers: { 'If-Match': String(opts.ifMatch) } } : {}),
    put: <T>(url: string, body: unknown, opts: { ifMatch?: number } = {}) => call<T>('PUT', url, body, opts.ifMatch !== undefined ? { headers: { 'If-Match': String(opts.ifMatch) } } : {}),
    /** DELETE, optionally with a JSON body (e.g. an Apple authorization code for revocation on account deletion). */
    del: <T>(url: string, body?: unknown) => call<T>('DELETE', url, body),
    page: async <T>(url: string, params?: Record<string, unknown>): Promise<Page<T>> => {
      const r = await send<T[]>({ method: 'GET', url, params: clean(params) });
      return { items: r.data, nextCursor: r.meta?.pagination?.nextCursor ?? null };
    },
    blob: async (url: string, method: 'GET' | 'POST' = 'GET', body?: unknown) => (await send<ArrayBuffer>({ method, url, data: body, responseType: 'arraybuffer' })).data,
    text: async (url: string, method: 'GET' | 'POST' = 'GET', body?: unknown) => (await send<string>({ method, url, data: body, responseType: 'text' })).data,
  };
}
export type Client = ReturnType<typeof createClient>;

function clean(p?: Record<string, unknown>) {
  if (!p) return undefined;
  return Object.fromEntries(Object.entries(p).filter(([, v]) => v !== undefined && v !== null && v !== ''));
}

export function toApiError(e: unknown): ApiError {
  if (e instanceof ApiError) return e;
  const ax = e as AxiosError<any>;
  if (ax?.isAxiosError) {
    if (!ax.response) return new ApiError(0, 'NETWORK', 'Keine Verbindung. Bitte prüfe dein Internet.');
    let body = ax.response.data;
    if (body instanceof ArrayBuffer) {
      try {
        body = JSON.parse(new TextDecoder().decode(body));
      } catch {
        body = null;
      }
    } else if (typeof body === 'string') {
      try {
        body = JSON.parse(body);
      } catch {
        body = null;
      }
    }
    const er = body?.error;
    return new ApiError(ax.response.status, er?.code ?? 'INTERNAL', er?.message ?? 'Da ist etwas schiefgelaufen.', er?.field, er?.details, typeof er?.key === 'string' ? er.key : undefined, er?.params && typeof er.params === 'object' ? er.params : undefined);
  }
  return new ApiError(0, 'INTERNAL', (e as Error)?.message ?? 'Unbekannter Fehler');
}

/** Unique key for the Idempotency-Key header (mobile offline queue, double clicks). */
export const newIdempotencyKey = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
